import { CheckCircle2, Clock, type LucideIcon, RefreshCw, XCircle } from 'lucide-react';

// Backend session status → the designer's status vocabulary (colour + icon).
// Backend values: InProgress | PendingApproval | Completed | Rejected.
interface StatusMeta {
  label: string;
  /** Icon + text colour. */
  color: string;
  /** Circle fill for the icon-only badge. */
  background: string;
  /** Circle border for the icon-only badge. */
  border: string;
  Icon: LucideIcon;
}

// The three colours per status are the designer's `.sc-card-badge-circle--*` triples from
// SessionCard.css, matched to our statuses by intent: green for completed, blue for in
// progress, red for rejected, and their orange for pending approval.
const META: Record<string, StatusMeta> = {
  Completed: {
    label: 'Completed',
    color: '#10B981',
    background: '#DCFCE7',
    border: '#86EFAC',
    Icon: CheckCircle2,
  },
  PendingApproval: {
    label: 'Pending',
    color: '#BF3636',
    background: '#BF36360D',
    border: '#FDBA74',
    Icon: Clock,
  },
  // Static circular arrow, matching the design's Status-In-Progress.svg and the sessions
  // list. Nothing in the design animates, and a running spinner would claim the page is
  // loading rather than that the applicant has not finished.
  InProgress: {
    label: 'In Progress',
    color: '#0048FF',
    background: '#EFF6FF',
    border: '#BFDBFE',
    Icon: RefreshCw,
  },
  Rejected: {
    label: 'Rejected',
    color: '#EF4444',
    background: '#FEE2E2',
    border: '#BF3636',
    Icon: XCircle,
  },
};

function statusMeta(status: string): StatusMeta {
  return (
    META[status] ?? {
      label: status || 'Pending',
      color: '#BF3636',
      background: '#BF36360D',
      border: '#FDBA74',
      Icon: Clock,
    }
  );
}

/**
 * The designer's `.sc-card-badge-circle` — a round, filled badge holding ONLY the status
 * icon, with the status name on hover.
 *
 * The list previously showed a full text pill ("In Progress", "Completed"). On a
 * ~300px-wide panel that pill competed with the applicant's name for the same row, so a
 * long name truncated to make room for a word that repeats on nearly every card. The
 * designer's own session card resolves it exactly this way: name on the left, a 20-24px
 * icon circle on the right.
 *
 * `title` carries the name, so nothing is lost — and `aria-label` carries it for a screen
 * reader, which cannot see a colour and cannot hover.
 */
export function StatusIconBadge({ status }: { status: string }) {
  const { label, color, background, border, Icon } = statusMeta(status);
  return (
    <span
      title={label}
      // `role="img"` so the `aria-label` is actually honoured — on a bare span it is
      // dropped, because a generic element has no name to give. The badge IS the content
      // (an icon standing for a word), which is exactly what this role describes.
      role="img"
      aria-label={label}
      className="flex h-[clamp(20px,1.5vw,24px)] w-[clamp(20px,1.5vw,24px)] shrink-0 items-center justify-center rounded-full border"
      style={{ color, backgroundColor: background, borderColor: border }}
    >
      <Icon size={13} className="shrink-0" />
    </span>
  );
}

// Circular initial avatar (we render applicant names, not photos).
export function InitialAvatar({ name, size = 28 }: { name: string; size?: number }) {
  return (
    <span
      className="flex shrink-0 items-center justify-center rounded-full bg-[#E0E7FF] font-semibold text-[#4F46E5]"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.42) }}
    >
      {(name?.charAt(0) ?? '?').toUpperCase()}
    </span>
  );
}
